"use client";

import React from "react";
import { BrainCircuit, Zap, Microscope, ChartLine, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const engineFeatures = [{
  title: "Custom LLM Integration",
  description: "Bespoke language models optimized for your specific business data and industry requirements to ensure brand-safe, accurate interactions.",
  icon: BrainCircuit,
  link: "#custom-llms"
}, {
  title: "Intelligent Automation",
  description: "Autonomous agents that handle high-volume repetitive tasks, significantly reducing operational overhead while improving overall efficiency.",
  icon: Zap,
  link: "#intelligent-automation"
}, {
  title: "Predictive Analytics",
  description: "Advanced data processing that identifies emerging trends and opportunities, turning raw information into actionable business intelligence.",
  icon: ChartLine,
  link: "#predictive-analytics"
}, {
  title: "Scalable AI Strategy",
  description: "Tailored roadmaps for SMBs to integrate artificial intelligence seamlessly into existing workflows without disrupting core business functions.",
  icon: Microscope,
  link: "#scalable-strategy"
}];
export default function TheNeuralEngine() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="129545" id="our-ai-toolkit" className="bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10" ref={ref}>
        <div className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <h2 className="text-3xl md:text-4xl font-default font-semibold mb-6 tracking-tight text-foreground">
            Our AI Toolkit
          </h2>
          <p className="text-sm font-default text-muted-foreground max-w-2xl mx-auto">
            Precision-engineered solutions designed to scale your small or medium business through the power of intelligent automation and data-driven insights.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {engineFeatures.map((feature, i) => <Link to={feature.link} key={i} data-index={i} className="block group outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm">
              <Card className={`h-full !bg-white border-primary/20 hover:border-primary/60 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden relative rounded-sm ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            transitionDelay: `${i * 150}ms`
          }}>
                <CardHeader className="pb-4">
                  <div className="mb-6 inline-flex p-3 rounded-sm bg-muted ring-1 ring-primary/10 group-hover:ring-primary/30 transition-all duration-300">
                    {}
                    <feature.icon className="w-8 h-8 text-primary" />
                  </div>
                  <CardTitle className="text-xl font-default font-semibold text-foreground">
                    {feature.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm font-default text-muted-foreground leading-relaxed mb-8">
                    {feature.description}
                  </p>
                  
                  {}
                  <div className="flex items-center gap-2 text-accent font-default font-semibold opacity-0 -translate-x-6 transition-all duration-300 ease-out group-hover:opacity-100 group-hover:translate-x-0">
                    <span className="text-xs tracking-wide uppercase">Explore Solution</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </CardContent>
              </Card>
            </Link>)}
        </div>
      </div>
    </section>;
}